import React, { useState, useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  Button,

  FormControlLabel,
  Checkbox,
  IconButton,
  Paper,
  
  Chip,
  Box
} from '@mui/material';
import { TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Add as AddIcon, Delete as DeleteIcon, Schedule as ScheduleIcon } from '@mui/icons-material';
import {  useDispatch } from 'react-redux';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import moment from 'moment';

import { showSuccessSnackbar, showErrorSnackbar } from 'store/actions/snackbarMessage';
import API from 'util/API';
import { authMiddleWare } from 'util/auth';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: '#f7f7f7',
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  card: {
    marginBottom: theme.spacing(3),
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  daySection: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    border: '1px solid #e0e0e0',
    borderRadius: theme.spacing(1),
  },
  timePeriod: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#f9f9f9',
    borderRadius: theme.spacing(1),
  },
  timeInput: {
    minWidth: '120px',
  },
  addButton: {
    marginTop: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  saveButton: {
    backgroundColor: '#4caf50',
    color: 'white',
    minWidth: '150px',
    '&:hover': {
      backgroundColor: '#388e3c',
    },
  },
  backButton: {
    backgroundColor: '#f44336',
    color: 'white',
    minWidth: '150px',
    '&:hover': {
      backgroundColor: '#d32f2f',
    },
  },
  dayChip: {
    margin: theme.spacing(0.5),
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  },
  enabledDay: {
    backgroundColor: '#4caf50',
    color: 'white',
  }
}));

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

const MenuServiceAvailability = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { menuId } = useParams();

  const [serviceAvailability, setServiceAvailability] = useState({});
  const [loading, setLoading] = useState(false);
  const [menuData, setMenuData] = useState(null);

  const showSuccess = (msg) => dispatch(showSuccessSnackbar(msg));
  const showError = (msg) => dispatch(showErrorSnackbar(msg));

  useEffect(() => {
    // Get menu data from navigation state
    if (location.state && location.state.menu) {
      setMenuData(location.state.menu);
      loadServiceAvailabilityFromMenuData(location.state.menu);
    } else {
      // Fallback: initialize with default structure
      initializeDefaultAvailability();
    }
  }, [menuId, location.state]);

  const initializeDefaultAvailability = () => {
    const defaultAvailability = {};
    DAYS_OF_WEEK.forEach(day => {
      defaultAvailability[day.key] = {
        enabled: false,
        time_periods: []
      };
    });
    setServiceAvailability(defaultAvailability);
  };

  const loadServiceAvailabilityFromMenuData = (menu) => {
    if (menu && menu.serviceAvailability) {
      const formattedAvailability = {};
      
      // Convert API format to component format
      menu.serviceAvailability.forEach(dayAvailability => {
        formattedAvailability[dayAvailability.day_of_week] = {
          enabled: true,
          time_periods: dayAvailability.time_periods.map((period, index) => ({
            id: `${dayAvailability.day_of_week}-${index}`,
            start_time: period.start_time,
            end_time: period.end_time
          }))
        };
      });
      
      // Fill in missing days
      DAYS_OF_WEEK.forEach(day => {
        if (!formattedAvailability[day.key]) {
          formattedAvailability[day.key] = {
            enabled: false,
            time_periods: []
          };
        }
      });
      
      setServiceAvailability(formattedAvailability);
    } else {
      initializeDefaultAvailability();
    }
  };

  const toggleDayEnabled = (dayKey) => {
    setServiceAvailability(prev => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        enabled: !prev[dayKey]?.enabled,
        time_periods: prev[dayKey]?.time_periods || []
      }
    }));
  };

  const addTimePeriod = (dayKey) => {
    const newPeriod = {
      id: Date.now(),
      start_time: '09:00',
      end_time: '17:00'
    };

    setServiceAvailability(prev => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        enabled: true,
        time_periods: [...(prev[dayKey]?.time_periods || []), newPeriod]
      }
    }));
  };

  const updateTimePeriod = (dayKey, periodId, field, value) => {
    setServiceAvailability(prev => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        time_periods: prev[dayKey].time_periods.map(period =>
          period.id === periodId ? { ...period, [field]: value } : period
        )
      }
    }));
  };

  const removeTimePeriod = (dayKey, periodId) => {
    setServiceAvailability(prev => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        time_periods: prev[dayKey].time_periods.filter(period => period.id !== periodId)
      }
    }));
  };

  const handleSave = async () => {
    authMiddleWare(history);
    setLoading(true);

    try {
      // Format data for API
      const formattedData = Object.entries(serviceAvailability)
        .filter(([_, dayData]) => dayData.enabled && dayData.time_periods.length > 0)
        .map(([dayKey, dayData]) => ({
          day_of_week: dayKey,
          time_periods: dayData.time_periods.map(period => ({
            start_time: period.start_time,
            end_time: period.end_time
          }))
        }));

      // Update only the service availability field
      await API.put(`/myMenu/${menuId}`, {
        serviceAvailability: formattedData
      });
      
      showSuccess('Service availability updated successfully');
      history.push(`/menu/${menuId}`);
    } catch (error) {
      if (error.response && error.response.status === 403) {
        history.push('/login');
      }
      console.log(error);
      showError('Failed to update service availability');
    } finally {
      setLoading(false);
    }
  };

  const formatTimeFromDate = (date) => {
    return moment(date).format('HH:mm');
  };

  const parseTimeToDate = (timeString) => {
    return moment(timeString, 'HH:mm').toDate();
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h3" component="h1" gutterBottom>
          Service Availability
          {menuData && (
            <Typography variant="h4" component="span" color="primary" style={{ marginLeft: '16px' }}>
              - {menuData.menuName}
            </Typography>
          )}
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Configure when your menu is available for each day of the week
        </Typography>
      </div>

      <Card className={classes.card}>
        <CardHeader
          avatar={<ScheduleIcon />}
          title="Weekly Service Hours"
          subheader="Set availability for each day of the week"
        />
        <CardContent>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={3}>
              {DAYS_OF_WEEK.map((day) => {
                const dayData = serviceAvailability[day.key] || { enabled: false, time_periods: [] };
                
                return (
                  <Grid item xs={12} key={day.key}>
                    <Paper className={classes.daySection}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={dayData.enabled}
                              onChange={() => toggleDayEnabled(day.key)}
                              color="primary"
                            />
                          }
                          label={
                            <Typography variant="h6">
                              {day.label}
                            </Typography>
                          }
                        />
                        <Chip
                          label={dayData.enabled ? 'Available' : 'Closed'}
                          className={clsx(classes.dayChip, dayData.enabled && classes.enabledDay)}
                        />
                      </Box>

                      {dayData.enabled && (
                        <>
                          {dayData.time_periods.map((period) => (
                            <div key={period.id} className={classes.timePeriod}>
                              <TimePicker
                                label="Start Time"
                                value={parseTimeToDate(period.start_time)}
                                onChange={(date) => 
                                  updateTimePeriod(day.key, period.id, 'start_time', formatTimeFromDate(date))
                                }
                                format="HH:mm"
                                ampm={false}
                                className={classes.timeInput}
                              />
                              <Typography variant="body2">to</Typography>
                              <TimePicker
                                label="End Time"
                                value={parseTimeToDate(period.end_time)}
                                onChange={(date) => 
                                  updateTimePeriod(day.key, period.id, 'end_time', formatTimeFromDate(date))
                                }
                                format="HH:mm"
                                ampm={false}
                                className={classes.timeInput}
                              />
                              <IconButton
                                onClick={() => removeTimePeriod(day.key, period.id)}
                                size="small"
                                color="secondary"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </div>
                          ))}
                          
                          <Button
                            startIcon={<AddIcon />}
                            onClick={() => addTimePeriod(day.key)}
                            className={classes.addButton}
                            size="small"
                          >
                            Add Time Period
                          </Button>
                        </>
                      )}
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </LocalizationProvider>
        </CardContent>
      </Card>

      <Box display="flex" justifyContent="space-between" mt={3}>
        <Button
          variant="contained"
          className={classes.backButton}
          onClick={() => history.push(`/menu/${menuId}`)}
        >
          Back to Menu
        </Button>
        
        <Button
          variant="contained"
          className={classes.saveButton}
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Service Availability'}
        </Button>
      </Box>
    </div>
  );
};

export default MenuServiceAvailability; 